import { UIState } from "@hydra/core/uiState";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class ContactUsModal implements Plugin {
  private unsubscribeHeaderSticked?: () => void;
  private unsubscribeAtPageEnd?: () => void;
  private unsubscribeCurrentBreakpoint?: () => void;
  private isModalOpen = false;
  private isMobile = false;
  private lastScrollY = 0;
  private scrollIntentAccumulator = 0;
  private isFloatingButtonVisible = true;
  private readonly initialVisibleScrollOffset = 280;
  private readonly scrollDeltaThreshold = 14;
  private readonly handleScroll = () => {
    this.updateButtonVisibility(UIState.getInstance());
  };

  constructor(private element: HTMLElement) {}

  init(): void {
    const uiState = UIState.getInstance();
    this.isMobile = uiState.isMobile || uiState.isTablet;
    this.lastScrollY = window.scrollY;

    this.unsubscribeHeaderSticked = uiState.subscribe("isHeaderSticked", () => {
      this.updateButtonVisibility(uiState);
    });

    this.unsubscribeAtPageEnd = uiState.subscribe("isAtPageEnd", () => {
      this.updateButtonVisibility(uiState);
    });

    this.initModalEvents(uiState);
    this.updateButtonVisibility(uiState);
    window.addEventListener("scroll", this.handleScroll, { passive: true });

    this.unsubscribeCurrentBreakpoint = uiState.subscribe(
      "currentBreakpoint",
      () => {
        this.initModalEvents(uiState);
      },
    );

    this.initCheckboxIcons();
  }

  open(): void {
    const backdrop = this.element.querySelector(
      ".dsc-co-contactUs-backdrop",
    ) as HTMLElement;
    backdrop.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    this.isModalOpen = true;
  }

  private initModalEvents(uiState: UIState): void {
    const isMobile = uiState.isMobile || uiState.isTablet;
    const backdrop = this.element.querySelector(
      ".dsc-co-contactUs-backdrop",
    ) as HTMLElement;
    const modal = this.element.querySelector(
      ".dsc-co-contactUs",
    ) as HTMLElement;
    const openButtons = document.querySelectorAll(
      ".dsc-plugin-contact-us-modal-trigger",
    );
    const closeBtn = this.element.querySelector(".close-modal") as HTMLElement;

    if (!backdrop || !modal || !closeBtn) return;

    modal.classList.remove(
      isMobile ? "contactUs-desktop-animation" : "contactUs-mobile-animation",
    );
    modal.classList.add(
      isMobile ? "contactUs-mobile-animation" : "contactUs-desktop-animation",
    );

    const openModal = () => {
      backdrop.classList.remove("hidden");
      document.body.style.overflow = "hidden";
      this.isModalOpen = true;
    };

    openButtons.forEach((btn) => {
      btn.addEventListener("click", openModal);
    });

    const closeModal = () => {
      backdrop.classList.add("hidden");
      document.body.style.overflow = "auto";
      this.isModalOpen = false;
    };

    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) closeModal();
    });

    closeBtn.addEventListener("click", closeModal);
  }

  private updateButtonVisibility(uiState: UIState): void {
    const floatingButton = this.element.querySelector(
      ".dsc-co-contactUs__button-fixed",
    ) as HTMLElement | null;
    if (!floatingButton) return;

    const currentScrollY = window.scrollY;
    const isInInitialVisibleRange =
      currentScrollY <= this.initialVisibleScrollOffset;
    const scrollDelta = currentScrollY - this.lastScrollY;
    const leftInitialRangeDownward =
      this.lastScrollY <= this.initialVisibleScrollOffset &&
      currentScrollY > this.initialVisibleScrollOffset;

    if (isInInitialVisibleRange) {
      this.isFloatingButtonVisible = true;
      this.scrollIntentAccumulator = 0;
    } else if (leftInitialRangeDownward) {
      this.isFloatingButtonVisible = false;
      this.scrollIntentAccumulator = 0;
    } else {
      const sameDirection =
        this.scrollIntentAccumulator === 0 ||
        scrollDelta === 0 ||
        Math.sign(scrollDelta) === Math.sign(this.scrollIntentAccumulator);

      if (sameDirection) {
        this.scrollIntentAccumulator += scrollDelta;
      } else {
        this.scrollIntentAccumulator = scrollDelta;
      }

      if (Math.abs(this.scrollIntentAccumulator) >= this.scrollDeltaThreshold) {
        this.isFloatingButtonVisible = this.scrollIntentAccumulator < 0;
        this.scrollIntentAccumulator = 0;
      }
    }

    this.lastScrollY = currentScrollY;

    const shouldShowButton =
      this.isFloatingButtonVisible && !uiState.isAtPageEnd;

    if (shouldShowButton) {
      floatingButton.classList.remove("dsc-co-contactUs__button-fixed--hidden");
      return;
    }

    floatingButton.classList.add("dsc-co-contactUs__button-fixed--hidden");
  }

  private initCheckboxIcons(): void {
    const checkboxes = this.element.querySelectorAll<HTMLInputElement>(
      ".dsc-co-contactUsFormCard__checkbox",
    );

    checkboxes.forEach((checkbox) => {
      const id = checkbox.id;
      const label = document.querySelector(`label[for="${id}"]`);
      if (!label) {
        return;
      }

      const iconChecked = label.querySelector<HTMLElement>(
        ".checkbox-icon-checked",
      );
      const iconUnchecked = label.querySelector<HTMLElement>(
        ".checkbox-icon-unchecked",
      );

      const toggleIcons = () => {
        if (checkbox.checked) {
          iconChecked?.classList.remove("hidden");
          iconUnchecked?.classList.add("hidden");
        } else {
          iconChecked?.classList.add("hidden");
          iconUnchecked?.classList.remove("hidden");
        }
      };

      toggleIcons();

      checkbox.addEventListener("change", toggleIcons);
    });
  }

  destroy(): void {
    window.removeEventListener("scroll", this.handleScroll);
    if (this.unsubscribeHeaderSticked) {
      this.unsubscribeHeaderSticked();
      this.unsubscribeHeaderSticked = undefined;
    }
    if (this.unsubscribeAtPageEnd) {
      this.unsubscribeAtPageEnd();
      this.unsubscribeAtPageEnd = undefined;
    }
    if (this.unsubscribeCurrentBreakpoint) {
      this.unsubscribeCurrentBreakpoint();
      this.unsubscribeCurrentBreakpoint = undefined;
    }
  }
}

PluginRegistry.register("contact-us-modal", ContactUsModal);
